// app/javascript/controllers/invitation_filters_controller.js
import { Controller } from "@hotwired/stimulus"

const LS_KEY = "invitation:favorites" // same key as card_selector_controller
export default class extends Controller {
    static targets = ["sidebar", "sortForm", "form", "categories"]

    connect() {
        // cache current charity_id from the URL
        const url = new URL(window.location.href)
        this.charityId = url.searchParams.get("charity_id") || ""

        // Sync categories disabled state on load (for "Make your Own")
        const form = this.formTarget
        if (form) {
            const typeInput   = form.querySelector('input[name="type"]:checked')
            const currentType = typeInput?.value || "all"
            this.currentType  = currentType
            this._setCategoriesDisabled(["custom", "community"].includes(currentType))
        }
    }

    toggle() {
        if (!this.hasSidebarTarget) return
        this.sidebarTarget.classList.toggle("d-none")
    }

    onSelectChange(event) {
        // Same idea for any dedicated select-controls form
        const form = event.currentTarget?.form || this.sortFormTarget
        if (!form) return

        const val = event.currentTarget.value
        const sortInput  = form.querySelector('input[name="sort"]')
        const photoInput = form.querySelector('input[name="photo_uploadable"]')

        if (val === "photo") {
            if (sortInput)  sortInput.value = ""
            if (photoInput) photoInput.value = "1"
        } else {
            if (sortInput)  sortInput.value = val
            if (photoInput) photoInput.value = ""
        }

        const pageHidden = form.querySelector('input[name="page"]')
        if (pageHidden) pageHidden.remove()

        this._ensureHidden(form, "charity_id", this.charityId)
        form.submit()
    }

    onTypeClick(event) {
        const input = event.target
        if (!(input instanceof HTMLInputElement) || input.name !== "type") return

        const form = this.formTarget
        if (!form) return

        const clickedValue = input.value
        const previousType = this.currentType || "all"
        const allInput     = form.querySelector('input[name="type"][value="all"]')

        // Case 1: user clicks again on the already-selected non-"all" type
        // → toggle back to "Show All"
        if (clickedValue !== "all" && clickedValue === previousType) {
            event.preventDefault()

            // Force radio selection back to "all"
            if (allInput) {
                allInput.checked = true
            }
            // Make sure the clicked radio is not selected
            input.checked = false

            this.currentType = "all"
            this._setCategoriesDisabled(false)
            this._submitForm(form)
            return
        }

        // Case 2: user selects a different type (or all for the first time)
        this.currentType = clickedValue
        this._setCategoriesDisabled(clickedValue === "custom")
        // Do not submit here; the form's change->submit handler will handle it.
    }

    filterAnonByFavorites() {
        let favs = []
        try { favs = JSON.parse(localStorage.getItem(LS_KEY) || "[]") } catch(_) {}

        const cards = document.querySelectorAll("[data-invitation-slug]")
        let shown = 0
        cards.forEach(node => {
            const slug = node.getAttribute("data-invitation-slug")
            const show = favs.includes(slug)
            node.classList.toggle("d-none", !show)
            if (show) shown++
        })

        // Update “results” counter if you have one
        const counter = document.querySelector(".results-count .p2")
        if (counter) counter.textContent = `${shown} results`

        // Empty state
        this.ensureEmptyState(shown === 0)
    }

    onCategoryChange(event) {
        const input = event.target
        if (!(input instanceof HTMLInputElement) || input.name !== "category_ids[]") return

        const form = this.formTarget
        if (!form) return

        // If this checkbox is being checked, uncheck all the others.
        if (input.checked) {
            form.querySelectorAll('input[name="category_ids[]"]').forEach(el => {
                if (el !== input) el.checked = false
            })
        }
        // We intentionally do NOT submit here; the form's change->submit
        // will run afterwards with the updated checked state.
    }

    ensureEmptyState(empty) {
        let box = document.getElementById("no-favorites-empty")
        if (!empty) { box?.remove(); return }

        if (!box) {
            box = document.createElement("div")
            box.id = "no-favorites-empty"
            box.className = "text-center my-5"
            box.innerHTML = `
        <p class="p2 mb-3">You haven’t favourited any invitations yet.</p>
        <p class="p2">Tap the <i class="bi bi-heart"></i> heart on designs you like, then choose “My favourites”.</p>
      `
            const gridRow = document.querySelector("[data-controller='card-selector']")
            gridRow?.parentNode?.insertBefore(box, gridRow.nextSibling)
        }
    }


    submit(event) {
        // Use the form that emitted the change; fallback to first target
        const f =
            (event?.currentTarget instanceof HTMLFormElement && event.currentTarget) ||
            event?.target?.closest?.("form") ||
            this.formTarget

        const type = f.querySelector('input[name="type"]:checked')?.value

        // Always drop pagination when changing filters
        this._removeInputsByName(f, "page")

        // Preserve charity_id from URL (cached in connect)
        this._ensureHidden(f, "charity_id", this.charityId)

        // If "custom" (Make your Own) -> nuke other filters before submit
        if (type === "custom") {
            ["category_ids[]", "q[]", "color", "sort", "photo_uploadable"].forEach(n =>
                this._removeInputsByName(f, n)
            )
        }

        this._submitForm(f)
    }

    submitSort(event) {
        // Prefer the <form> that contains the changed <select>
        const f = event.currentTarget?.form || this.sortFormTarget

        const val = event.currentTarget.value

        // Anonymous "favorites" stays client-side
        const grid = document.querySelector("[data-controller='card-selector']")
        const hasUser = grid?.dataset.cardSelectorCurrentUserIdValue?.length > 0
        if (val === "favorites" && !hasUser) {
            event.preventDefault()
            this.filterAnonByFavorites()
            const url = new URL(window.location)
            url.searchParams.set("sort", "favorites")
            history.replaceState({}, "", url)
            return
        }

        this._removeInputsByName(f, "page")
        this._ensureHidden(f, "charity_id", this.charityId)
        this._submitForm(f)
    }

    _submitForm(form) {
        if (!form) return
        form.submit()
    }

    _removeInputsByName(form, name) {
        form.querySelectorAll(`[name="${CSS.escape(name)}"]`).forEach(el => el.remove())
    }

    _ensureHidden(form, name, value) {
        if (!value) return
        let el = form.querySelector(`input[type="hidden"][name="${CSS.escape(name)}"]`)
        if (!el) {
            el = document.createElement("input")
            el.type = "hidden"
            el.name = name
            form.appendChild(el)
        }
        el.value = value
    }

    _setCategoriesDisabled(disabled) {
        const form = this.formTarget
        if (!form) return

        const boxes = form.querySelectorAll('input[name="category_ids[]"]')
        boxes.forEach(box => {
            box.disabled = disabled
            if (disabled) box.checked = false
        })

        if (this.hasCategoriesTarget) {
            this.categoriesTarget.classList.toggle("filters-categories--disabled", disabled)
        }
    }

}